<template>
  <nuxt-link
    v-if="nuxt"
    v-ripple="computedRipple"
    :to="to"
    :disabled="isDisabled"
    :class="classObject"
    :type="type"
    :replace="replace"
    :append="append"
    :active-class="activeClass"
    :exact="exact"
    :exact-active-class="exactActiveClass"
    :size="size"
    tag="button"
    @click="$emit('click', true)"
  >
    <button-inner
      :has-left-icon="hasLeftIcon"
      :has-right-icon="hasRightIcon"
      :is-loading="isLoading"
      :loading-text="loadingText"
    >
      <slot />
    </button-inner>
  </nuxt-link>

  <router-link
    v-else-if="vRouter"
    v-ripple="computedRipple"
    :to="to"
    :disabled="isDisabled"
    :class="classObject"
    :type="type"
    :replace="replace"
    :append="append"
    :active-class="activeClass"
    :exact="exact"
    :exact-active-class="exactActiveClass"
    :size="size"
    tag="button"
    @click="$emit('click', true)"
  >
    <button-inner
      :has-left-icon="hasLeftIcon"
      :has-right-icon="hasRightIcon"
      :is-loading="isLoading"
      :loading-text="loadingText"
    >
      <slot />
    </button-inner>
  </router-link>

  <button
    v-else-if="!nuxt"
    v-ripple="computedRipple"
    :href="href"
    :disabled="isDisabled"
    :class="classObject"
    :type="type"
    @click="$emit('click', true)"
  >
    <button-inner
      :has-left-icon="hasLeftIcon"
      :has-right-icon="hasRightIcon"
      :is-loading="isLoading"
      :loading-text="loadingText"
    >
      <slot />
    </button-inner>
  </button>
</template>

<script>
import "@smartdesign/core/css/elements/buttons.css";
import ripple from "../../directives/ripple";

import buttonInner from "./buttonInner";

export default {
  components: {
    buttonInner,
  },

  directives: {
    ripple,
  },

  props: {
    /**
     * button href link
     */
    href: {
      required: false,
      type: String,
      default: undefined,
    },

    /**
     * button size `50`, `100`, `200`
     */
    size: {
      required: false,
      type: Number,
      default: 100,
      validator: (val) => {
        return [50, 100, 200].indexOf(val) !== -1;
      },
    },

    /**
     * button variant: `primary`, `secondary`, `tertiary`
     * is required if variants are set dynamically
     */
    variant: {
      required: false,
      type: String,
      default: "primary",
      validator: (val) => {
        return ["primary", "secondary", "tertiary"].indexOf(val) !== -1;
      },
    },

    /**
     * button variant: `primary`. override prop variant
     */
    primary: {
      required: false,
      type: Boolean,
      default: undefined,
    },

    /**
     * button variant: `secondary`. override prop variant
     */
    secondary: {
      required: false,
      type: Boolean,
      default: undefined,
    },

    /**
     * button variant: `tertiary`. override prop variant
     */
    tertiary: {
      required: false,
      type: Boolean,
      default: undefined,
    },

    /**
     * button danger state
     */
    isDanger: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * button on dark state
     */
    isOnDark: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * set button to disabled state
     */
    isDisabled: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * button has a left icon
     * @value icon name
     */
    hasLeftIcon: {
      required: false,
      type: String,
      default: "",
    },

    /**
     * button has a right icon
     * @value icon name
     */
    hasRightIcon: {
      required: false,
      type: String,
      default: "",
    },

    /**
     * loading animation
     */
    isLoading: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * button hast full width
     */
    hasFullWidth: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * button hast full width on certain viewport
     */
    fullWidthOn: {
      required: false,
      type: String,
      default: undefined,
      validator: (val) => {
        return ["100", "200", "300", "400"].indexOf(val) !== -1;
      },
    },

    /**
     * type
     */
    type: {
      required: false,
      type: String,
      default: "button",
      validator: (val) => {
        return ["button", "reset", "submit"].indexOf(val) !== -1;
      },
    },

    /**
     * ripple
     */
    ripple: {
      required: false,
      type: Boolean,
      default: true,
    },

    /**
     * Text for screen readers while isLoading = true
     */
    loadingText: {
      required: false,
      type: String,
      default: "wird geladen",
    },

    /**
     * Specifies the link is a `nuxt-link`
     * See Nuxt Link: https://nuxtjs.org/api/components-nuxt-link/
     */
    nuxt: {
      required: false,
      type: Boolean,
      default: undefined,
    },

    /**
     * Specifies the link is a `router-link`
     */
    vRouter: {
      required: false,
      type: Boolean,
      default: undefined,
    },

    /**
     * button href link
     */
    to: {
      required: false,
      type: [Object, String],
      default: undefined,
    },

    /**
     * See Vue Router: https://router.vuejs.org/api/#replace
     */
    replace: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * See Vue Router: https://router.vuejs.org/api/#append
     */
    append: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * See Vue Router: https://router.vuejs.org/api/#active-class
     */
    activeClass: {
      required: false,
      type: String,
      default: "router-link-active",
    },

    /**
     * See Vue Router: https://router.vuejs.org/api/#append
     */
    exact: {
      required: false,
      type: Boolean,
      default: false,
    },

    /**
     * See Vue Router: https://router.vuejs.org/api/#exact-active-class
     */
    exactActiveClass: {
      required: false,
      type: String,
      default: "router-link-exact-active",
    },
  },

  computed: {
    computedRipple() {
      if (!this.ripple) return "off";
      if (this.computedVariant === "secondary" && (this.isOnDark || this.isDanger)) return "dark";
      if (this.computedVariant === "secondary") return "light";
      if (this.isOnDark) return "light";
      return "dark";
    },

    computedVariant() {
      if (this.primary) return "primary";
      if (this.secondary) return "secondary";
      if (this.tertiary) return "tertiary";
      return this.variant;
    },

    classObject() {
      return {
        btn: true,

        // size
        "btn--50": this.size === 50,
        "btn--100": this.size === 100,
        "btn--200": this.size === 200,

        // variant
        "btn--primary": this.computedVariant === "primary",
        "btn--secondary": this.computedVariant === "secondary",
        "btn--tertiary": this.computedVariant === "tertiary",

        //modifiers
        "btn--danger": this.isDanger,
        "btn--on-dark": this.isOnDark,

        //right icon
        "btn--icon-right": this.hasRightIcon !== "",

        // is-loading
        "btn--loading": this.isLoading,

        // full width
        "btn--full-width": this.hasFullWidth,
        "btn--full-width:400": this.fullWidthOn === "400",
        "btn--full-width:300": this.fullWidthOn === "300",
        "btn--full-width:200": this.fullWidthOn === "200",
        "btn--full-width:100": this.fullWidthOn === "100",
      };
    },
  },
};
</script>
